import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Show, useUser } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { LazyVideo } from "@/components/ui/lazy-video";
import { VideoModal } from "@/components/ui/video-modal";
import { AuthModal } from "@/components/ui/auth-modal";
import { api, buildQueryString } from "@/lib/api";
import { useUserStore } from "@/stores/user-store";
import { useFreeH3PromoStatus } from "@/pages/free-gen-page";
import { isExpired } from "@/config/retention";
import type { Video as VideoType } from "@/types";
import {
  Sparkles,
  ArrowRight,
  Wand2,
  Type,
  Clapperboard,
  Video,
  Palette,
  BookOpen,
  DollarSign,
  Users,
  Play,
  Zap,
  Gift,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";

const TOOLS = [
  {
    href: "/create/text-to-image",
    icon: Type,
    title: "Text to Image",
    desc: "Type a description, get an image",
    gradient: "from-orange-500/10 to-amber-500/10",
    hoverGradient: "group-hover:from-orange-500/20 group-hover:to-amber-500/20",
    iconColor: "text-orange-500",
    iconGlow: "group-hover:shadow-orange-500/20",
    accentBorder: "group-hover:border-orange-300/60",
  },
  {
    href: "/create/image-edit",
    icon: Wand2,
    title: "Image Edit",
    desc: "Transform any image with AI",
    gradient: "from-rose-500/10 to-pink-500/10",
    hoverGradient: "group-hover:from-rose-500/20 group-hover:to-pink-500/20",
    iconColor: "text-rose-500",
    iconGlow: "group-hover:shadow-rose-500/20",
    accentBorder: "group-hover:border-rose-300/60",
  },
  {
    href: "/create/text-to-video",
    icon: Video,
    title: "Text to Video",
    desc: "Describe a scene, get a video",
    gradient: "from-emerald-500/10 to-teal-500/10",
    hoverGradient: "group-hover:from-emerald-500/20 group-hover:to-teal-500/20",
    iconColor: "text-emerald-500",
    iconGlow: "group-hover:shadow-emerald-500/20",
    accentBorder: "group-hover:border-emerald-300/60",
  },
  {
    href: "/create/image-to-video",
    icon: Clapperboard,
    title: "Image to Video",
    desc: "Bring any image to life",
    gradient: "from-blue-500/10 to-indigo-500/10",
    hoverGradient: "group-hover:from-blue-500/20 group-hover:to-indigo-500/20",
    iconColor: "text-blue-500",
    iconGlow: "group-hover:shadow-blue-500/20",
    accentBorder: "group-hover:border-blue-300/60",
  },
];

function ToolCards() {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 stagger-children">
      {TOOLS.map((tool) => (
        <Link
          key={tool.href}
          to={tool.href}
          className={cn(
            "group relative flex flex-col gap-3 p-4 rounded-2xl border border-white/50 bg-white/50 backdrop-blur-sm overflow-hidden",
            "hover-lift",
            tool.accentBorder,
          )}
        >
          <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500", tool.gradient)} />
          <div className="relative flex items-center justify-between">
            <div className={cn("size-10 rounded-xl flex items-center justify-center bg-white/80 shadow-sm transition-shadow duration-300", tool.iconGlow)}>
              <tool.icon className={cn("size-[18px] group-hover-bounce", tool.iconColor)} strokeWidth={1.8} />
            </div>
            <ArrowRight className="size-3.5 text-[#c5c0b0] group-hover:text-[#2d3127] group-hover:translate-x-1 transition-all duration-300" />
          </div>
          <div className="relative min-w-0">
            <h3 className="text-[13px] font-bold text-[#2d3127] mb-0.5">{tool.title}</h3>
            <p className="text-[11px] text-[#8a8570] leading-snug hidden sm:block">{tool.desc}</p>
          </div>
        </Link>
      ))}
    </div>
  );
}

function PromptBar() {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate(`/create/text-to-image${prompt.trim() ? `?prompt=${encodeURIComponent(prompt.trim())}` : ""}`);
  };

  return (
    <form onSubmit={handleSubmit} className="relative max-w-xl">
      <div className="relative flex items-center rounded-2xl bg-white/80 backdrop-blur-sm border border-white/60 shadow-[0_2px_12px_rgba(45,49,39,0.06)] hover:shadow-[0_4px_20px_rgba(45,49,39,0.08)] prompt-bar-focus transition-all duration-300">
        <Palette className="size-4 text-[#b5b0a0] ml-4 shrink-0" />
        <input
          type="text"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Describe what you want to create..."
          className="flex-1 bg-transparent px-3 py-3.5 text-sm text-[#2d3127] placeholder:text-[#b5b0a0] focus:outline-none"
        />
        <button
          type="submit"
          className="mr-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-xs font-bold hover:from-[#e8822e] hover:to-[#d4712a] transition-all duration-200 flex items-center gap-1.5 shadow-sm hover:shadow-md hover:shadow-orange-500/15"
        >
          <Sparkles className="size-3" />
          Create
        </button>
      </div>
    </form>
  );
}

function MonetizeCallout() {
  return (
    <Link
      to="/monetize"
      className="group relative flex items-center gap-4 p-5 rounded-2xl border border-emerald-200/40 bg-gradient-to-r from-emerald-50/50 to-teal-50/30 backdrop-blur-sm overflow-hidden hover-lift"
    >
      <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative size-12 rounded-2xl bg-white/80 shadow-sm flex items-center justify-center shrink-0 group-hover:shadow-emerald-500/20 transition-shadow duration-300">
        <DollarSign className="size-5 text-emerald-600 group-hover-bounce" strokeWidth={2} />
      </div>
      <div className="relative flex-1 min-w-0">
        <h3 className="text-sm font-bold text-[#2d3127] mb-0.5">Learn How to Monetize Your Videos</h3>
        <p className="text-[12px] text-[#8a8570]">
          Step-by-step playbook to turn your AI creations into income
        </p>
      </div>
      <ArrowRight className="relative size-4 text-emerald-400 group-hover:text-emerald-600 group-hover:translate-x-1.5 transition-all duration-300 shrink-0" />
    </Link>
  );
}

const GUIDE_STYLES = [
  { label: "Abstract", image: "/guide/abstract.png" },
  { label: "Fractal", image: "/guide/fractal.png" },
  { label: "Watercolour", image: "/guide/watercolour.png" },
  { label: "B&W", image: "/guide/bw.png" },
];

function PromptingGuideCallout() {
  return (
    <Link
      to="/prompting-guide"
      className="group relative block rounded-2xl border border-orange-200/30 bg-gradient-to-br from-orange-50/40 to-amber-50/20 backdrop-blur-sm overflow-hidden hover-lift"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative flex flex-col sm:flex-row items-center gap-5 p-5">
        <div className="flex gap-2.5 shrink-0">
          {GUIDE_STYLES.map((s) => (
            <div key={s.label} className="relative group/img">
              <img
                src={s.image}
                alt={s.label}
                className="size-14 sm:size-16 rounded-xl object-cover border border-white/60 shadow-sm group-hover/img:scale-105 transition-transform duration-300"
                loading="lazy"
              />
              <span className="absolute bottom-1 left-1 right-1 text-center text-[7px] font-bold uppercase tracking-wider text-white bg-black/40 backdrop-blur-sm rounded-md px-1 py-0.5">
                {s.label}
              </span>
            </div>
          ))}
        </div>
        <div className="flex-1 min-w-0 text-center sm:text-left">
          <div className="flex items-center gap-2 justify-center sm:justify-start mb-1">
            <BookOpen className="size-4 text-orange-500" strokeWidth={2} />
            <h3 className="text-sm font-bold text-[#2d3127]">Prompting Guide</h3>
          </div>
          <p className="text-[12px] text-[#8a8570] leading-relaxed mb-2">
            Sample prompts, tips, and techniques to level up your creations.
          </p>
          <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-orange-500 group-hover:text-orange-600 transition-colors">
            Read the guide <ArrowRight className="size-3 group-hover:translate-x-1.5 transition-transform duration-300" />
          </span>
        </div>
      </div>
    </Link>
  );
}

const USER_CREATIONS_S3 = "https://couchstackdata.s3.us-east-1.amazonaws.com/user-creations";

const USER_CREATIONS_PREVIEW = [
  `${USER_CREATIONS_S3}/c178527f-8b8e-4a3e-8d7d-105d71d9e20d.mp4`,
  `${USER_CREATIONS_S3}/64ceb468-f4a7-4c78-af2b-ad3b689c4951.mp4`,
  `${USER_CREATIONS_S3}/bff8d6ad-8e78-4052-a157-395dbbdfc591.mp4`,
  `${USER_CREATIONS_S3}/c264bb8f-2e7d-401b-a7b9-f285b1e41249.mp4`,
  `${USER_CREATIONS_S3}/6d1dfff0-a58c-420e-9050-03e2dccae80e.mp4`,
  `${USER_CREATIONS_S3}/969c9a29-51b5-4319-84b4-254ec0b7c511.mp4`,
  `${USER_CREATIONS_S3}/2119c128-e0af-4aa8-b517-75b4f1e452a6.mp4`,
  `${USER_CREATIONS_S3}/cf3d2237-8be6-4783-a77b-59ff6004c544.mp4`,
];

function UserCreationsSection() {
  const [activeVideo, setActiveVideo] = useState<string | null>(null);
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-xl bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center shadow-sm">
            <Users className="size-4 text-violet-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-base font-extrabold text-[#2d3127] tracking-tight">User Creations</h2>
            <p className="text-[11px] text-[#8a8570]">Made by real people using CouchStack</p>
          </div>
        </div>
        <Link
          to="/user-creations"
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-violet-600 bg-violet-50/80 hover:bg-violet-100 border border-violet-200/40 transition-all duration-200 hover:shadow-sm"
        >
          View all <ArrowRight className="size-3" />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 stagger-children">
        {USER_CREATIONS_PREVIEW.map((video, i) => (
          <div
            key={i}
            onClick={() => setActiveVideo(video)}
            className="video-card group"
          >
            <div className="relative w-full aspect-square bg-[#f0ebe0] overflow-hidden">
              <LazyVideo src={video} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div className="size-11 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30 shadow-lg scale-75 group-hover:scale-100 transition-transform duration-300">
                  <Play className="size-4 text-white ml-0.5" fill="white" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {activeVideo && <VideoModal src={activeVideo} onClose={() => setActiveVideo(null)} />}
    </div>
  );
}

function MyRecentCreations() {
  const { user, isSignedIn } = useUser();
  const { hasPurchased } = useUserStore();
  const { data } = useQuery({
    queryKey: ["home-recent-videos", user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error("No user");
      return api.get<{ success: boolean; videos: VideoType[] }>(
        `/videos${buildQueryString({ user_id: user.id, limit: "6" })}`,
      );
    },
    enabled: !!user?.id && isSignedIn,
    refetchInterval: (query) => {
      const vids = query.state.data?.videos ?? [];
      const inFlight = vids.some((v) => v.status === "pending" || v.status === "processing");
      return inFlight ? 3000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const videos = data?.videos ?? [];
  const inFlight = videos.filter((v) => v.status === "pending" || v.status === "processing");
  const completed = videos.filter((v) => v.status === "completed" && (v.url || v.watermarked_url) && !isExpired(v.created_at));
  const recent = [...inFlight, ...completed].slice(0, 3);

  if (!isSignedIn || recent.length === 0) return null;

  return (
    <section className="px-5 md:px-8 pb-6 max-w-5xl mx-auto animate-fade-in">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="size-9 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center shadow-sm">
            <Clapperboard className="size-4 text-orange-600" strokeWidth={2} />
          </div>
          <div>
            <h2 className="text-base font-extrabold text-[#2d3127] tracking-tight">Your Recent Creations</h2>
            <p className="text-[11px] text-[#8a8570]">
              {inFlight.length > 0 ? "Generating — updates automatically" : "Pick up where you left off"}
            </p>
          </div>
        </div>
        <Link
          to="/assets"
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold text-orange-600 bg-orange-50/80 hover:bg-orange-100 border border-orange-200/40 transition-all duration-200 hover:shadow-sm"
        >
          My Creations <ArrowRight className="size-3" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-2.5 sm:gap-3">
        {recent.map((v) => {
          const isBusy = v.status === "pending" || v.status === "processing";
          if (isBusy) {
            return (
              <Link
                key={v.id}
                to={`/generation/${v.id}`}
                className="video-card group"
              >
                <div className="relative w-full aspect-square bg-[#f0ebe0] overflow-hidden flex flex-col items-center justify-center gap-2">
                  <Loader2 className="size-6 text-[#e8822e] animate-spin" />
                  <span className="text-[11px] font-semibold text-[#8a8570]">Generating…</span>
                </div>
              </Link>
            );
          }
          const displayUrl = hasPurchased
            ? (v.url || v.watermarked_url)
            : (v.watermarked_url || v.url);
          return (
            <Link key={v.id} to="/assets" className="video-card group">
              <div className="relative w-full aspect-square bg-[#f0ebe0] overflow-hidden">
                {v.media_type === "IMAGE" ? (
                  <img src={displayUrl ?? undefined} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <LazyVideo src={displayUrl ?? ""} className="w-full h-full object-cover" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            </Link>
          );
        })}
      </div>
      {!hasPurchased && (
        <Link
          to="/billing"
          className="mt-4 flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-sm font-bold shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-px transition-all duration-200"
        >
          <Sparkles className="size-4" />
          Buy a pack to generate!
          <ArrowRight className="size-4" />
        </Link>
      )}
    </section>
  );
}

export function HomePage() {
  const { user } = useUser();
  const { hasPurchased } = useUserStore();
  const { data: promo } = useFreeH3PromoStatus(user?.id);
  const showFreeGenNotice = !!promo?.eligible && hasPurchased;

  return (
    <div className="overflow-hidden relative">
      {/* Full-page ambient background */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
        <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-orange-300/15 blur-[120px] blob-1" />
        <div className="absolute -top-16 right-[-5%] w-[400px] h-[400px] rounded-full bg-violet-300/10 blur-[100px] blob-2" />
        <div className="absolute top-[40%] left-[10%] w-[350px] h-[350px] rounded-full bg-emerald-300/8 blur-[100px] blob-2" />
        <div className="absolute top-[60%] right-[5%] w-[300px] h-[300px] rounded-full bg-amber-200/10 blur-[120px] blob-1" />
        <div className="absolute bottom-[10%] left-[30%] w-[400px] h-[400px] rounded-full bg-rose-200/8 blur-[120px] blob-2" />
      </div>

      {showFreeGenNotice && (
        <section className="relative px-5 md:px-8 pt-6 max-w-5xl mx-auto animate-fade-in-up">
          <Link
            to="/free-gen"
            className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 rounded-2xl border-2 border-[#e8822e]/50 bg-gradient-to-r from-[#fff4e8] via-[#ffe8d4] to-[#fff4e8] px-5 py-4 shadow-[0_0_0_1px_rgba(232,130,46,0.08)] hover:border-[#e8822e] transition-colors"
          >
            <div className="size-11 rounded-xl bg-[#e8822e] text-white flex items-center justify-center shrink-0">
              <Gift className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-wider text-[#e8822e] mb-0.5">
                Limited time · Purchasers only
              </p>
              <p className="text-[17px] font-extrabold text-[#2d3127] tracking-tight">
                Free Generation — 15 Second
              </p>
              <p className="text-[12px] text-[#8a8570] mt-0.5">
                One free Enhanced video with sound. Claim it before it&apos;s gone.
              </p>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white bg-[#e8822e] px-3.5 py-2 rounded-xl shrink-0 self-start sm:self-center">
              Claim free gen
              <ArrowRight className="size-3.5" />
            </span>
          </Link>
        </section>
      )}

      {/* Hero */}
      <section className="relative px-5 md:px-8 pt-10 md:pt-14 pb-8 max-w-5xl mx-auto">

        <div className="relative flex flex-col md:flex-row items-center gap-8 md:gap-12">
          <div className="flex-1 text-center md:text-left">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-50/80 border border-orange-200/40 text-[11px] font-semibold text-orange-600 mb-4 animate-fade-in-up">
              <Zap className="size-3" fill="currentColor" />
              Powered by the latest AI models
            </div>
            <h1 className="text-3xl md:text-[46px] font-extrabold text-[#2d3127] tracking-tight leading-[1.08] mb-4 animate-fade-in-up delay-75">
              Make Something<br />
              Cool with <span className="text-gradient-animated">AI</span>
            </h1>
            <p className="text-[#8a8570] text-[15px] max-w-md mb-7 animate-fade-in-up delay-150 leading-relaxed">
              Create AI art and videos you'll actually want to share with your friends and family. No experience needed.
            </p>
            <div className="animate-fade-in-up delay-200">
              <PromptBar />
            </div>
          </div>
          <div className="shrink-0 mascot-animate animate-fade-in delay-300">
            <img
              src="/couch-mascot.png?v=5"
              alt="CouchStack mascot"
              className="w-48 md:w-64"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* Tools */}
      <section className="px-5 md:px-8 pb-6 max-w-5xl mx-auto animate-fade-in-up delay-300">
        <ToolCards />
      </section>

      {/* Your recent creations (signed-in, has generations) */}
      <MyRecentCreations />

      {/* Callouts row */}
      <section className="px-5 md:px-8 pb-6 max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 animate-fade-in delay-400">
          <MonetizeCallout />
          <PromptingGuideCallout />
        </div>
      </section>

      {/* User Creations */}
      <section className="px-5 md:px-8 pb-10 max-w-5xl mx-auto animate-fade-in delay-500">
        <UserCreationsSection />
      </section>

      {/* Grass CTA */}
      <section className="relative mt-8 animate-fade-in delay-700">
        <div className="grass-blades" />
        <div className="grass-section pt-14 pb-16">
          <div className="relative max-w-3xl mx-auto px-5 md:px-8 text-center">
            <h2 className="text-xl md:text-2xl font-extrabold text-white tracking-tight mb-2">Start creating art</h2>
            <p className="text-sm text-white/70 mb-7 max-w-sm mx-auto">42 free stars on signup. No subscription required.</p>
            <Show when="signed-out">
              <CtaAuthButton />
            </Show>
            <Show when="signed-in">
              <Link
                to="/create/text-to-image"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-white text-[#4a6741] text-sm font-bold hover:bg-[#f5f0e5] transition-all duration-200 shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/15 hover:-translate-y-0.5"
              >
                Start Creating <ArrowRight className="size-3.5" />
              </Link>
            </Show>
          </div>
        </div>
      </section>
    </div>
  );
}

function CtaAuthButton() {
  const [showAuth, setShowAuth] = useState(false);
  return (
    <>
      <button
        onClick={() => setShowAuth(true)}
        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-white text-[#4a6741] text-sm font-bold hover:bg-[#f5f0e5] transition-all duration-200 mx-auto shadow-lg shadow-black/10 hover:shadow-xl hover:shadow-black/15 hover:-translate-y-0.5"
      >
        <Sparkles className="size-3.5" />
        Get Started Free
      </button>
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
}
